import { VgEmploymentState } from '@interfaces';
import { createSelector } from '@ngrx/store';
import { FilteredClientAccount, YesNoEmpty } from '@shared/models';
import { Routes } from '@shared/models/routes.enum';
import {
  selectInheritanceDecedentSecurityToggle,
  selectInheritanceNewTermsToggle,
  selectInheritanceVCDToggle,
  selectMultiInheritanceToggle,
} from '@store/selectors/feature/feature.selectors';
import { selectIsCrewOnMultiInheritanceFlow } from '@store/selectors/multi-account/multi-account.selectors';
import { selectEmploymentMfeDataByOwnerIndex } from '@store/selectors/open-account/open-account.selectors';

import {
  getBeneficiariesState,
  getDecedentInfoState,
  getFeatureToggleState,
  getTriageState,
} from '../../../reducers';
import { BeneficiariesState } from '../../../reducers/beneficiaries/beneficiaries.reducer';
import { FeatureToggleState } from '../../../reducers/feature/feature.reducer';
import { ISelectedClientAccountsState } from '../../../reducers/selected-client-accounts/selected-client-accounts.reducer';
import { TriageState } from '../../../reducers/triage/triage.reducer';
import { selectEligibleIndividualAccounts } from '../../client-accounts/client-accounts.selectors';
import { selectedClientAccountsState } from '../../selected-client-accounts/selected-client-accounts.selectors';
import {
  selectIsClient,
  selectIsIndividualDesignationExecutor,
  selectIsIRASingleExecutor,
  selectisIRAToEstateFlow,
  selectIsTrustee,
  selectIsTrustToTrustFlow,
} from '../../triage/triage.selectors';
import { nullState } from '../common/router.selectors';
import {
  getInheritanceAssumeNextPage,
  getInheritanceBeneDetailsNextPage,
  getInheritanceBeneRelationshipNextPage,
  getInheritanceContactInfoNextPage,
  getInheritanceDecedentInfoRoute,
  getInheritanceEmploymentNextPage,
  getInheritanceEstateNextPage,
  getInheritanceIncomeNetWorthNextPage,
  getInheritanceInvestObjNextPage,
  getInheritanceIraTypeNextPage,
  getInheritanceLetterUploadNexPage,
  getInheritanceOverviewNextPage,
  getInheritancePersonalInfoNextPage,
  getInheritanceReviewNextPage,
  getInheritanceTCFTrustInfoNextPage,
  getInheritanceTCFTrustSignNextPage,
  getInheritanceTermsNextPage,
  getInheritanceTransferFromNextPage,
  getInheritanceTransferToNextPage,
  getInheritanceTriageNextPage,
  getInheritanceTrustInfoNextPage,
  getInheritanceTrustSettlementFundNextPage,
  getInheritanceTrustTaxInfoNextPage,
  getInheritanceUploadNextPage,
} from './inheritance-router.helpers';

export const selectInheritanceOverviewNextPage = createSelector(
  selectMultiInheritanceToggle,
  selectIsCrewOnMultiInheritanceFlow,
  (isMultiInheritanceFlow: boolean, isCrewOnMultiInheritanceFlow: YesNoEmpty) =>
    getInheritanceOverviewNextPage({
      isMultiInheritanceFlow,
      isCrewOnMultiInheritanceFlow,
    }),
);

export const selectMultiInheritanceOverviewNextPage = createSelector(
  nullState,
  selectInheritanceDecedentSecurityToggle,
  (nullState, inheritanceDecedentSecurityToggleEnabled) => {
    return inheritanceDecedentSecurityToggleEnabled
      ? Routes.MULTI_INHERITANCE_DECEDENT_INFORMATION
      : Routes.MULTI_INHERITANCE_DECEDENT_INFO;
  },
);

export const selectInheritanceTriageNextPage = createSelector(
  getTriageState,
  getFeatureToggleState,
  (triageState: TriageState, featureToggleState: FeatureToggleState) =>
    getInheritanceTriageNextPage(triageState, featureToggleState),
);
export const selectInheritanceDecedentInfoNextPage = createSelector(
  getDecedentInfoState,
  getTriageState,
  getFeatureToggleState,
  selectEligibleIndividualAccounts(),
  selectIsIndividualDesignationExecutor,
  selectisIRAToEstateFlow,
  selectIsTrustee,
  selectIsTrustToTrustFlow,
  (
    decedentInfoState,
    triageState,
    featureToggleState,
    eligibleAccounts,
    indvDesignationExecutor,
    isIRAToEstateFlow,
    isTrustFlow,
    isTrustToTrustFlow,
  ) =>
    getInheritanceDecedentInfoRoute(
      decedentInfoState,
      triageState,
      featureToggleState,
      eligibleAccounts,
      indvDesignationExecutor,
      isIRAToEstateFlow,
      isTrustFlow,
      isTrustToTrustFlow,
    ),
);

export const selectInheritanceUploadNextPage = createSelector(
  getTriageState,
  getFeatureToggleState,
  selectEligibleIndividualAccounts(),
  selectIsIndividualDesignationExecutor,
  selectIsTrustToTrustFlow,
  (
    triageState: TriageState,
    featureToggleState: FeatureToggleState,
    eligibleAccounts: FilteredClientAccount,
    indvDesignationExecutor: boolean,
    isTrustToTrustFlow: boolean,
  ) =>
    getInheritanceUploadNextPage(
      triageState,
      featureToggleState,
      eligibleAccounts,
      indvDesignationExecutor,
      isTrustToTrustFlow,
    ),
);
export const selectInheritanceIraTypeNextPage = createSelector(
  getTriageState,
  selectedClientAccountsState,
  (triageState: TriageState, selectedAccounts: ISelectedClientAccountsState) =>
    getInheritanceIraTypeNextPage(triageState, selectedAccounts),
);

export const selectInheritanceBeneRelationshipNextPage = createSelector(
  getBeneficiariesState,
  (beneficiariesState: BeneficiariesState) =>
    getInheritanceBeneRelationshipNextPage(beneficiariesState),
);
export const selectInheritanceTransferFromNextPage = createSelector(
  selectEligibleIndividualAccounts(),
  (individualAccounts: FilteredClientAccount) =>
    getInheritanceTransferFromNextPage(individualAccounts),
);

export const selectInheritanceTransferToNextPage = createSelector(
  selectedClientAccountsState,
  (selectedAccounts: ISelectedClientAccountsState) =>
    getInheritanceTransferToNextPage(selectedAccounts),
);

export const selectInheritanceAssumeNextPage = createSelector(
  getTriageState,
  selectedClientAccountsState,
  (triageState: TriageState, selectedAccounts: ISelectedClientAccountsState) =>
    getInheritanceAssumeNextPage(triageState, selectedAccounts),
);
export const selectInheritanceBeneDetailsNextPage = createSelector(nullState, () =>
  getInheritanceBeneDetailsNextPage(),
);
export const selectInheritancePersonalInfoNextPage = createSelector(nullState, () =>
  getInheritancePersonalInfoNextPage(),
);
export const selectInheritanceContactInfoNextPage = createSelector(
  selectIsIRASingleExecutor,
  selectIsTrustee,
  selectIsTrustToTrustFlow,
  selectIsClient,
  (
    isIRASingleExecutor: boolean,
    isTrustee: boolean,
    isTrustToTrustFlow: boolean,
    isClient: boolean,
  ) =>
    getInheritanceContactInfoNextPage(isIRASingleExecutor, isTrustee, isTrustToTrustFlow, isClient),
);
export const selectInheritanceEmploymentNextPage = createSelector(
  getTriageState,
  selectEmploymentMfeDataByOwnerIndex(0),
  (triageState: TriageState, employmentState: Partial<VgEmploymentState> | undefined) =>
    getInheritanceEmploymentNextPage(triageState, employmentState),
);
export const selectInheritanceLetterUploadNextPage = createSelector(nullState, () =>
  getInheritanceLetterUploadNexPage(),
);
export const selectInheritanceReviewNextPage = createSelector(
  selectedClientAccountsState,
  selectInheritanceNewTermsToggle,
  selectIsTrustToTrustFlow,
  (
    selectedAccounts: ISelectedClientAccountsState,
    isNewTermsEnabled: boolean,
    isTrustToTrustFlow: boolean,
  ) => getInheritanceReviewNextPage(selectedAccounts, isNewTermsEnabled, isTrustToTrustFlow),
);

export const selectInheritanceInvestObjNextPage = createSelector(nullState, () =>
  getInheritanceInvestObjNextPage(),
);
export const selectInheritanceIncomeNetWorthNextPage = createSelector(nullState, () =>
  getInheritanceIncomeNetWorthNextPage(),
);
export const selectInheritanceEstateNextPage = createSelector(
  getTriageState,
  (triageState: TriageState) => getInheritanceEstateNextPage(triageState),
);

export const selectInheritanceTermsNextPage = createSelector(nullState, () =>
  getInheritanceTermsNextPage(),
);

export const selectInheritanceTrustInfoNextPage = createSelector(nullState, () =>
  getInheritanceTrustInfoNextPage(),
);

export const selectInheritanceTrustTaxInfoNextPage = createSelector(
  selectInheritanceVCDToggle,
  (isInheritanceVCDToggle: boolean) => getInheritanceTrustTaxInfoNextPage(isInheritanceVCDToggle),
);

export const selectInheritanceTCFTrustInfoNextPage = createSelector(nullState, () =>
  getInheritanceTCFTrustInfoNextPage(),
);

export const selectInheritanceTCFTrustSignNextPage = createSelector(nullState, () =>
  getInheritanceTCFTrustSignNextPage(),
);

export const selectInheritanceTrustSettlementFundNextPage = createSelector(nullState, () =>
  getInheritanceTrustSettlementFundNextPage(),
);
